import React from 'react';

import { Text } from './Text';

import type { SectionLabelProps, SectionLabelSize } from './SectionLabel.types';
import type { TextVariant } from './Text.types';

/**
 * Size rank → the type variant it rides on.
 */
const SIZE_VARIANT: Record<SectionLabelSize, TextVariant> = {
  xs: 'label-xs',
  sm: 'label-sm',
};

/**
 * Size rank → the uppercase treatment layered over the variant.
 *
 * Both ranks override the label scale's own 0.02em tracking, which is why they
 * are set here rather than left to the type token. At 10-12px, uppercase needs
 * more air than 0.02em (≈0.2px) gives it — set at the scale's default the caps
 * collide and the label reads as a smudge rather than as a label.
 *
 * The two ranks deliberately keep different tracking. `xs` is the loud eyebrow
 * and takes the full `tracking-widest`; `sm` is the quieter band divider, and
 * widening it too would change the rhythm of every Reports section header —
 * a visual decision that does not belong in a de-duplication change.
 */
const SIZE_TREATMENT: Record<SectionLabelSize, string> = {
  xs: 'font-semibold uppercase tracking-widest',
  sm: 'font-medium uppercase tracking-wide',
};

/**
 * SectionLabel - the uppercase muted label that heads a section, card or page.
 *
 * The single canonical spelling of a pattern that had drifted into nine across
 * the app (`tracking-wide` / `tracking-widest` / four different arbitrary em
 * values, at two sizes and two weights). Composing this instead of hand-rolling
 * the class string is what keeps the ranks legible: `xs` is an eyebrow, `sm` is
 * a band divider, and there is no third option.
 *
 * Always muted (`neutral-500`) — a section label is chrome, and anything that
 * needs to be louder than its own content is a heading, not a label. Use
 * `Heading` for that.
 *
 * @example
 * // Page kicker above an h1
 * <SectionLabel>Training plan</SectionLabel>
 *
 * // Card section heading, in the document outline
 * <SectionLabel as="h2">How this gets tracked</SectionLabel>
 *
 * // Band divider between panel groups
 * <SectionLabel size="sm" as="h2">Recovery</SectionLabel>
 *
 * // One step of a flow, its place said first
 * <SectionLabel step="01 / 02">Scan the code</SectionLabel>
 */
export function SectionLabel({
  size = 'xs',
  as = 'div',
  step,
  className = '',
  'data-testid': testId,
  children,
}: SectionLabelProps): React.JSX.Element {
  return (
    <Text
      variant={SIZE_VARIANT[size]}
      color="muted"
      as={as}
      className={`${SIZE_TREATMENT[size]} ${className}`.trim()}
      data-testid={testId}
    >
      {step ? (
        <span className="mr-2 font-mono text-primary-300">{step}</span>
      ) : null}
      {children}
    </Text>
  );
}

export default SectionLabel;
